/**
 * IT Admin → Knowledge (9 Oct): the pieces every page shares — the access
 * gate, the status / review / index badges, a plain select, and the area
 * picker.
 */

import { Alert, Badge, Select } from "@norican/react";
import type { ReactNode } from "react";

import { LoadingState, PageContainer } from "@/components/common";
import { useT } from "@/i18n";
import { useMe } from "@/lib/api";

import { type AreaLike, areaOptions, indexTone, reviewTone, statusTone } from "./lib/knowledge";

export interface KnowledgeGateProps {
  title: string;
  /** Bulk add and Areas: only `KNOWLEDGE_EDITORS` (`knowledge_editor`). */
  editorsOnly?: boolean;
  children: ReactNode;
}

/**
 * Every IT user reads (`it_admin`); editors write. The server applies the
 * same rule (403), this only says it nicely.
 */
export function KnowledgeGate({ title, editorsOnly = false, children }: KnowledgeGateProps) {
  const { t } = useT();
  const { data: me, isPending } = useMe();
  if (isPending) {
    return (
      <PageContainer title={title}>
        <LoadingState />
      </PageContainer>
    );
  }
  const allowed = me?.it_admin === true && (!editorsOnly || me.knowledge_editor === true);
  if (!allowed) {
    return (
      <PageContainer title={title}>
        <Alert tone="neutral">
          {editorsOnly ? t("knowledgeAdmin.editorsOnly") : t("knowledgeAdmin.notAllowed")}
        </Alert>
      </PageContainer>
    );
  }
  return <>{children}</>;
}

export function StatusBadge({ status }: { status: string }) {
  const { t } = useT();
  return (
    <Badge tone={statusTone(status)} variant="subtle" size="sm">
      {t(`knowledgeAdmin.status.${status as "draft" | "published" | "retired"}`)}
    </Badge>
  );
}

export function ReviewBadge({ state }: { state: string }) {
  const { t } = useT();
  const tone = reviewTone(state);
  if (tone === null) {
    return null;
  }
  return (
    <Badge tone={tone} variant="subtle" size="sm">
      {t(`knowledgeAdmin.review.${state as "overdue" | "due_soon"}`)}
    </Badge>
  );
}

export function IndexBadge({ state }: { state: string }) {
  const { t } = useT();
  const tone = indexTone(state);
  if (tone === null) {
    return null;
  }
  return (
    <Badge tone={tone} variant="outline" size="sm">
      {t(`knowledgeAdmin.index.${state as "failed" | "pending"}`)}
    </Badge>
  );
}

export function AudienceBadge({ audience }: { audience: string }) {
  const { t } = useT();
  if (audience !== "it") {
    return null;
  }
  return (
    <Badge tone="accent" variant="outline" size="sm">
      {t("knowledgeAdmin.audience.it")}
    </Badge>
  );
}

export interface OptionSelectProps {
  options: readonly { value: string; label: string }[];
  value: string;
  onValueChange: (value: string) => void;
  placeholder: string;
  "aria-label"?: string;
  disabled?: boolean;
}

/** A single-choice dropdown over `{ value, label }`; "" is the empty choice. */
export function OptionSelect({
  options,
  value,
  onValueChange,
  placeholder,
  "aria-label": ariaLabel,
  disabled = false,
}: OptionSelectProps) {
  return (
    <Select.Root
      value={value === "" ? null : value}
      items={[...options]}
      disabled={disabled}
      onValueChange={(next) => onValueChange(next == null ? "" : String(next))}
    >
      <Select.Trigger placeholder={placeholder} aria-label={ariaLabel} />
      <Select.Portal>
        <Select.Positioner sideOffset={4}>
          <Select.Popup width="anchor">
            <Select.List>
              {options.map((option) => (
                <Select.Item key={option.value} value={option.value}>
                  {option.label}
                </Select.Item>
              ))}
            </Select.List>
          </Select.Popup>
        </Select.Positioner>
      </Select.Portal>
    </Select.Root>
  );
}

export interface AreaPickerProps {
  areas: readonly AreaLike[];
  value: readonly string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}

/** Several areas: the chosen ones as removable badges, plus "Add an area…". */
export function AreaPicker({ areas, value, onChange, disabled = false }: AreaPickerProps) {
  const { t } = useT();
  const byId = new Map(areas.map((area) => [area.id, area.path]));
  const remaining = areaOptions(areas).filter((option) => !value.includes(option.value));
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-1">
        {value.length === 0 ? (
          <span className="text-fg-muted text-sm">{t("knowledgeAdmin.areas.none")}</span>
        ) : (
          value.map((id) => (
            <Badge
              key={id}
              tone="accent"
              variant="subtle"
              size="sm"
              onRemove={disabled ? undefined : () => onChange(value.filter((v) => v !== id))}
              removeLabel={t("knowledgeAdmin.areas.remove")}
            >
              {byId.get(id) ?? id}
            </Badge>
          ))
        )}
      </div>
      {disabled ? null : (
        <OptionSelect
          options={remaining}
          value=""
          onValueChange={(next) => {
            if (next !== "") {
              onChange([...value, next]);
            }
          }}
          placeholder={t("knowledgeAdmin.areas.add")}
          aria-label={t("knowledgeAdmin.areas.add")}
        />
      )}
    </div>
  );
}
